import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Stack } from '@mui/material'
import Button from 'components/forms/Button'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import SelectionSelect from 'components/forms/specific/SelectionSelect'
import ExportAction from 'export/actions/ExportAction'
import { getLogin } from 'utils/SettingUtils'
import moment from 'moment'
import DtoInstallation from 'installation/dto/installation/DtoInstallation'
import StationAction from 'station/actions/StationAction'
import { getStationTitleWithTownName } from 'utils/StationUtils'
import ProgressCard from '../../../components/card/ProgressCard'
import { genericPromise } from '../../../utils/ActionUtils'
import ApplicationConf from '../../../conf/ApplicationConf'
import SimpleDatePicker from '../../../components/forms/SimpleDatePicker'
import ToastrAction from '../../../toastr/actions/ToastrAction'
import { template, orderBy } from 'lodash'
import { arrayOf } from '../../../utils/StoreUtils'
import DtoFileFTP from 'station/dto/DtoFileFTP'
import useTitle from 'utils/customHook/useTitle'

const HOLD = 'ATTENTE'
const INPROGRESS = 'ENCOURS'
const ERROR = 'ERROR'
const SUCCESS = 'SUCCESS'

const InstallationsExport = ({
    stationTypes,
}) => {
    const {
        installation,
        filesFtp,
        citiesIndex,
    } = useSelector(store => ({
        installation: store.InstallationReducer.installation,
        filesFtp: store.StationReducer.filesFtp,
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)

    const dispatch = useDispatch()

    const [count, setCount] = useState(0)
    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)
    const [selection, setSelection] = useState(-1)
    const [dialogIsOpen, setDialogIsOpen] = useState(false)
    const [calculationStatus, setCalculationStatus] = useState(HOLD)

    const townName = useMemo(() => citiesIndex[installation.townCode]?.name, [citiesIndex, installation])

    useTitle(() => [{
        title: i18n[stationTypes[0]],
        href: stationTypes[0],
    }, {
        title: getStationTitleWithTownName(installation, townName),
        href: `station/${stationTypes[0]}/${installation.id}`,
    }, {
        title: i18n.export,
        href: `station/${stationTypes[0]}/'${installation.id}/export`,
    }], [installation, townName])

    useEffect(() => {
        dispatch(StationAction.fetchFilesFtp('DATA', `STEP_flux_${installation.code}*.csv`))
        const timeout = setTimeout(() => {
            dispatch(StationAction.fetchFilesFtp('DATA', `STEP_flux_${installation.code}*.csv`))
            setCount(count+1)
        }, 10000)

        return () => clearTimeout(timeout)
    }, [count])

    const launchExport = () => {
        const params = {
            idInstallation: installation.id,
            codeInstallation: installation.code,
            startDate,
            endDate,
            codeSelection: selection,
        }
        const exportTmpObj = {
            id: 0,
            exportType: 'stepFlow',
            login: getLogin(),
            creationDate: moment().valueOf(),
            status: 1,
            stationType: 'installation',
            params: JSON.stringify(params),
        }
        dispatch(ExportAction.runExportTmp(exportTmpObj))
    }

    const setParameterSelection = (_, newSelection) => {
        setSelection(newSelection)
    }

    const launchCalculation = () => {
        if (selection && selection != -1) {
            setCalculationStatus(INPROGRESS)
            genericPromise(ApplicationConf.quality.launchStepMoleculeSigniCalculation(), 'POST', {
                startDate,
                endDate,
                selectionCode: selection,
                codeSTEP: installation.code,
            }, {
                404: () => setCalculationStatus(ERROR),
                500: () => setCalculationStatus(ERROR),
            })
                .then(() => {
                    setCalculationStatus(SUCCESS)
                })
        } else {
            dispatch(ToastrAction.error(template(i18n.fieldNotSet)({ field: i18n.selection })))
        }
    }

    const calculationDialog = () => {
        return (
            <Dialog
                onClose={() => setDialogIsOpen(false)}
                fullWidth
                maxWidth='lg'
                open={dialogIsOpen}
            >
                <DialogTitle>{i18n.moleculeSigniCalculation}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={1}>
                        <Grid size={3}>
                            <SimpleDatePicker
                                value={startDate}
                                id={'startDate'}
                                label={i18n.beginDate}
                                onChange={setStartDate}
                            />
                        </Grid>
                        <Grid size={3}>
                            <SimpleDatePicker
                                value={endDate}
                                id={'endDate'}
                                label={i18n.endDate}
                                startDate={startDate}
                                onChange={setEndDate}
                            />
                        </Grid>
                        <Grid size={6}>
                            <SelectionSelect
                                value={selection}
                                onChange={setParameterSelection}
                            />
                        </Grid>
                        {
                            calculationStatus === HOLD ? (
                                <Grid size={3}>
                                    <Button
                                        title={i18n.recalculateSelection}
                                        onClick={launchCalculation}
                                        style={{ width: 400 }}
                                    />
                                </Grid>
                            ) : (calculationStatus === INPROGRESS ? (
                                <ProgressCard indeterminate message={i18n.calculationInProgressWithTime}/>
                            ) : (calculationStatus === SUCCESS ? (
                                <h5 style={{ color: 'green' }}>{ i18n.updateSuccess }</h5>
                            ) : (
                                <Stack>
                                    <h5 style={{ color: 'red' }}>{ `${i18n.error}`}</h5>
                                    <h5 style={{ color: 'red' }}>{ `${i18n.qualityMeterAssociatedWithThisWastewater}`}</h5>
                                    <h5 style={{ color: 'red' }}>{ `${i18n.otherwise} ${i18n.pleaseContactTheAdmin}`}</h5>
                                </Stack>
                            )))
                        }
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setDialogIsOpen(false)
                    }} title={i18n.close}
                    >
                        {i18n.close}
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    return (
        <Grid padding={1}>
            <Card>
                <CardContent>
                    <Grid container spacing={1}>
                        <Grid size={2}>
                            <SimpleDatePicker
                                value={startDate}
                                id={'startDate'}
                                label={i18n.beginDate}
                                onChange={setStartDate}
                            />
                        </Grid>
                        <Grid size={2} >
                            <SimpleDatePicker
                                value={endDate}
                                id={'endDate'}
                                label={i18n.endDate}
                                startDate={startDate}
                                onChange={setEndDate}
                            />
                        </Grid>
                        <Grid size={2} >
                            <SelectionSelect
                                value={selection}
                                onChange={setParameterSelection}
                            />
                        </Grid>
                        <Grid size={3} >
                            <Button
                                title={i18n.moleculeSigniCalculation}
                                onClick={() => {
                                    setCalculationStatus(HOLD)
                                    setDialogIsOpen(true)
                                }}
                                style={{ width: 400 }}
                            />
                        </Grid>
                        <Grid size={2} >
                            <Button
                                title={i18n.calculateFlux}
                                onClick={launchExport}
                                style={ { width: 200, marginLeft: 10 } }
                            />
                        </Grid>
                        <Grid size={12} style={{ marginBottom: 15 }}>
                            <Divider style={{ margin: 'auto', backgroundColor: '#161832' }} />
                        </Grid>
                        {filesFtp && (
                            <Grid size={12}>
                                <p style={{ fontSize: 16, padding: 5, margin: 0 }}>{i18n.exportList}</p>
                                {orderBy(filesFtp.filter(f => !f.isDirectory), 'updateDate', 'desc').map((d) => {
                                    return (
                                        <a key={d} style={{ padding: 5 }} href={d.url}>
                                            {d.name}
                                        </a>
                                    )
                                })}
                            </Grid>
                        )}
                        {calculationDialog()}
                    </Grid>
                </CardContent>
            </Card>
        </Grid>
    )
}

InstallationsExport.propTypes = {
    installation: PropTypes.instanceOf(DtoInstallation),
    filesFtp: arrayOf(DtoFileFTP),
    stationTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
}

export default InstallationsExport